import { newId, type ArbiterEvent, type BargeinClassifyResult, type BargeinOutcome, type BargeinTier, type Settings, type SttResult } from '@voicer/shared';
import type { Player } from '../audio/player';
import type { EchoGuard } from '../audio/echoGuard';
import { concatFloat32, trimAudioFromWallTime } from '../audio/wav';
import { classifyLexical, endsWithQuestion } from './lexical';

/** Wall-time marks attached to a hard interrupt / soft yield. */
export interface InterruptMarks {
  speechStart: number;
  speechEnd?: number;
  /** Audio before this instant is assistant bleed; the interrupt capture drops it. */
  cleanFrom?: number;
}

export interface ArbiterHooks {
  settings: () => Settings;
  player: Player;
  echoGuard: EchoGuard;
  activeTurnId: () => string | null;
  assistantActive: () => boolean;
  /** Current mic noise floor (RMS) for syllable-peak detection. */
  noiseFloor: () => number;
  stt: (audio: Float32Array) => Promise<SttResult>;
  classify: (turnId: string, transcript: string, currentSentence: string) => Promise<BargeinClassifyResult>;
  /** Stop everything and treat `transcript` (if any) as the start of a new user turn. */
  hardInterrupt: (turnId: string, tier: BargeinTier, transcript: string | null, marks: InterruptMarks) => void;
  /** Finish the current sentence, then treat `transcript` as the user's turn. */
  softYield: (turnId: string, tier: BargeinTier, transcript: string, marks: InterruptMarks & { speechEnd: number }) => void;
  aside: (turnId: string, transcript: string, outcome: BargeinOutcome, currentSentence: string) => void;
  /** Assistant finished while we were still deciding: route the transcript to the normal turn path. */
  handoffToMain: (transcript: string, marks: { speechStart: number; speechEnd: number }) => void;
  recordEvent: (ev: ArbiterEvent) => void;
  setState: (s: 'candidate' | 'ducked' | 'paused' | 'classifying' | 'speaking') => void;
  /** Drop elevated VAD thresholds while we listen after pause / stop (assistant no longer audible). */
  setPlaybackBias: (active: boolean) => void;
  /** Drop continuous barge-in mic capture (backchannel / noise / cancel). */
  clearCapture: () => void;
  log: (msg: string) => void;
}

interface Candidate {
  turnId: string;
  onsetTs: number;
  confirmed: boolean;
  frames: number;
  duckTs: number | null;
  /** Playback frozen because the speech looked sustained (not a cough / clap). */
  pauseTs: number | null;
  /** Wall time when TTS was stopped/paused — STT should prefer audio after this. */
  cleanFromTs: number | null;
  overlapTimer: number | null;
  hardInterrupted: boolean;
  /**
   * Soft echo suspicion at onset (TTS bleed). Unlike a hard reject, we still duck and can
   * promote to a real barge-in when speech evidence appears and correlation drops.
   */
  echoSuspect: boolean;
  /**
   * Playback was paused by the overlap gate while still echo-suspect (prefer-pause policy).
   * An empty transcript afterwards resumes playback instead of stopping the assistant.
   */
  echoPause: boolean;
  /** Noise floor frozen at onset so TTS bleed cannot raise the syllable threshold mid-candidate. */
  noiseFloorAtOnset: number;
  // Speech-evidence tracker (reset at duck so echo/sustained checks see post-duck frames only).
  totalFrames: number;
  speechFrames: number;
  energy: number[];
}

interface PendingSegment {
  audio: Float32Array;
  startTs: number;
  endTs: number;
}

/** The VAD segment being decided, plus the wall time from which its audio is clean of TTS. */
interface Segment extends PendingSegment {
  trimFrom: number;
}

interface Decision {
  outcome: BargeinOutcome;
  tier: BargeinTier;
  transcript: string;
  stt: SttResult | null;
  cls: BargeinClassifyResult | null;
}

/** VAD frame length (Silero v5 @ 16 kHz, 512 samples). */
const FRAME_MS = 32;

/**
 * Barge-in arbiter. Speech during assistant playback goes through staged gates:
 * echo suspect (soft) -> confirm onset (min frames) -> duck -> speech evidence -> pause ->
 * segment end -> fast STT -> lexical Tier A -> in-context Tier B.
 *
 * Important: TTS bleed must NOT permanently lock out barge-in. An echo-suspect onset still
 * ducks (so AEC/mic can hear the user) and promotes when sustained speech is detected.
 */
export class BargeInArbiter {
  private candidate: Candidate | null = null;
  /** True while STT/classify runs after speechEnd (candidate already cleared). */
  private deciding = false;
  private unclearCount = 0;
  private lastTurnForEscalation: string | null = null;

  constructor(private hooks: ArbiterHooks) {}

  get active(): boolean {
    return this.candidate !== null || this.deciding;
  }

  /** STT/classify of a segment is in flight; later VAD segments belong to the same capture. */
  get isDeciding(): boolean {
    return this.deciding;
  }

  get paused(): boolean {
    return this.candidate?.pauseTs !== null && this.candidate?.pauseTs !== undefined;
  }

  resetForTurn(turnId: string | null) {
    if (turnId !== this.lastTurnForEscalation) {
      this.unclearCount = 0;
      this.lastTurnForEscalation = turnId;
    }
  }

  /** Returns true when the onset was claimed by the arbiter (assistant was active). */
  onSpeechStart(ts: number): boolean {
    if (!this.hooks.assistantActive()) return false;
    // Already arbitrating / classifying this barge-in — keep the open capture, don't restart.
    if (this.candidate || this.deciding) return true;
    const turnId = this.hooks.activeTurnId() ?? 'unknown';
    this.resetForTurn(turnId);
    const s = this.hooks.settings();
    // Soft suspect only — never hard-reject at onset (that locked out real barge-in during TTS).
    const echoSuspect = this.hooks.echoGuard.isEcho(s.echoCorrelationThreshold, this.hooks.player.isAudible());
    this.candidate = {
      turnId,
      onsetTs: ts,
      confirmed: false,
      frames: 0,
      duckTs: null,
      pauseTs: null,
      cleanFromTs: null,
      overlapTimer: null,
      hardInterrupted: false,
      echoSuspect,
      echoPause: false,
      noiseFloorAtOnset: Math.max(this.hooks.noiseFloor(), 0.002),
      totalFrames: 0,
      speechFrames: 0,
      energy: [],
    };
    if (echoSuspect) {
      this.hooks.log(`onset echo-suspect (corr ${this.hooks.echoGuard.lastCorrelation.toFixed(2)}); will duck and re-check`);
    }
    this.hooks.setState('candidate');
    return true;
  }

  /** Called for every VAD frame; confirms the onset, arms the duration gate, and pauses on sustained speech. */
  onFrame(prob: number, micRms: number) {
    const c = this.candidate;
    if (!c || c.hardInterrupted) return;
    const s = this.hooks.settings();
    const speech = prob >= s.vadPositiveThreshold;
    c.totalFrames++;
    if (speech) c.speechFrames++;
    c.energy.push(micRms);
    if (c.energy.length > 200) c.energy.shift();

    // Re-evaluate echo each frame once we have a little history.
    if (c.echoSuspect && c.totalFrames >= 4) {
      const stillEcho = this.hooks.echoGuard.isEcho(s.echoCorrelationThreshold, this.hooks.player.isAudible());
      if (!stillEcho) {
        c.echoSuspect = false;
        this.hooks.log('echo-suspect cleared — treating as real barge-in candidate');
      }
    }

    if (!c.confirmed) {
      if (speech) c.frames++;
      // Duck even when echo-suspect: lowering far-end helps the mic / AEC hear the user.
      const need = c.echoSuspect ? s.bargeinMinFrames + 2 : s.bargeinMinFrames;
      if (c.frames >= need) {
        c.confirmed = true;
        c.duckTs = Date.now();
        this.hooks.player.duck();
        // Evaluate echo and sustained speech on post-duck frames only: the duck step drops mic
        // bleed and output RMS together, which keeps the old correlation window artificially high.
        this.hooks.echoGuard.resetWindow();
        c.totalFrames = 0;
        c.speechFrames = 0;
        c.energy = [];
        this.hooks.setState('ducked');
        const remaining = Math.max(50, s.overlapHardMs - (c.duckTs - c.onsetTs));
        c.overlapTimer = window.setTimeout(() => this.onOverlapExceeded(), remaining);
      }
      return;
    }

    // Time-boxed echo clearance: ducked TTS at duckLevel should not keep Silero positive for
    // bargeinPauseAfterMs at the configured speech ratio; if it does, it is the user.
    if (c.echoSuspect && c.duckTs !== null && Date.now() - c.duckTs >= s.bargeinPauseAfterMs) {
      const ratio = c.totalFrames > 0 ? c.speechFrames / c.totalFrames : 0;
      if (ratio >= s.bargeinSpeechRatio) {
        c.echoSuspect = false;
        this.hooks.log('echo-suspect cleared by sustained post-duck VAD ratio');
      }
    }

    if (c.pauseTs === null && this.looksLikeSustainedSpeech(c, s)) {
      // Sustained speech wins over echo suspicion.
      c.echoSuspect = false;
      c.pauseTs = Date.now();
      // Trim STT from duck/onset — NOT pause. Pause is hundreds of ms later and was clipping
      // the start of the user's interrupt ("wait…" → "…plain that again").
      c.cleanFromTs = c.duckTs ?? c.onsetTs;
      this.hooks.player.pause();
      this.hooks.setPlaybackBias(false);
      this.hooks.setState('paused');
      this.hooks.log(`paused playback after ${c.pauseTs - c.onsetTs} ms of sustained speech`);
    }
  }

  private looksLikeSustainedSpeech(c: Candidate, s: Settings): boolean {
    const elapsed = c.totalFrames * FRAME_MS;
    // Echo-suspect needs a bit more evidence before we freeze TTS.
    const minMs = c.echoSuspect ? s.bargeinPauseAfterMs + 150 : s.bargeinPauseAfterMs;
    const minRatio = c.echoSuspect ? Math.min(0.95, s.bargeinSpeechRatio + 0.1) : s.bargeinSpeechRatio;
    const minSyll = c.echoSuspect ? s.bargeinMinSyllables + 1 : s.bargeinMinSyllables;
    if (elapsed < minMs) return false;
    if (c.totalFrames === 0 || c.speechFrames / c.totalFrames < minRatio) return false;
    // Frozen at onset: TTS bleed must not raise the syllable threshold while we evaluate.
    return countSyllablePeaks(c.energy, c.noiseFloorAtOnset * 3) >= minSyll;
  }

  /**
   * Overlap gate. Never a no-op: when the onset still looks like echo we pause (reversible mute,
   * prefer-pause policy) and let the segment decide; otherwise hard-stop the assistant.
   */
  private onOverlapExceeded() {
    const c = this.candidate;
    if (!c || c.hardInterrupted) return;
    c.overlapTimer = null;
    const now = Date.now();
    if (c.echoSuspect && !this.looksLikeSustainedSpeech(c, this.hooks.settings())) {
      if (c.pauseTs === null) {
        c.pauseTs = now;
        c.echoPause = true;
        // Audio after the mute is clean; anything before is likely the assistant's own voice.
        c.cleanFromTs = now;
        this.hooks.player.pause();
        this.hooks.setPlaybackBias(false);
        this.hooks.setState('paused');
        this.hooks.log('overlap timer: still echo-suspect — pausing playback until the segment decides');
      }
      return;
    }
    c.hardInterrupted = true;
    // Keep speech from duck/onset — using `now` discarded everything said before the hard cut.
    c.cleanFromTs = c.cleanFromTs ?? c.duckTs ?? c.onsetTs;
    this.hooks.setPlaybackBias(false);
    this.record(c, '', 'duration', 'INTERRUPT', { decision: now, stop: now, stt: null, cls: null });
    this.hooks.hardInterrupt(c.turnId, 'duration', null, { speechStart: c.onsetTs, cleanFrom: c.cleanFromTs });
  }

  /** Segments that ended while STT/classify of the first one was still running. */
  private pendingSegments: PendingSegment[] = [];

  /** Called by the controller when a VAD segment ends while {@link isDeciding}. */
  queueSegmentWhileDeciding(audio: Float32Array, startTs: number, endTs: number) {
    if (!this.deciding) return;
    this.pendingSegments.push({ audio, startTs, endTs });
  }

  private takePendingSegments(): PendingSegment | null {
    if (!this.pendingSegments.length) return null;
    const segs = this.pendingSegments;
    this.pendingSegments = [];
    return {
      audio: concatFloat32(segs.map((s) => s.audio)),
      startTs: segs[0].startTs,
      endTs: segs[segs.length - 1].endTs,
    };
  }

  /**
   * Returns true when the segment was consumed by the arbiter; false means the caller should treat it
   * as a normal utterance. When false, use {@link takeMainLaneAudio} after a false return.
   */
  private mainLaneAudio: Float32Array | null = null;

  /** Audio for the main STT lane after a duration hard-interrupt (TTS prefix already dropped). */
  takeMainLaneAudio(): Float32Array | null {
    const a = this.mainLaneAudio;
    this.mainLaneAudio = null;
    return a;
  }

  /** Returns true when the segment was consumed by the arbiter; false means the caller should treat it as a normal utterance. */
  async onSpeechEnd(audio: Float32Array, startTs: number, endTs: number): Promise<boolean> {
    const c = this.candidate;
    if (!c) return false;
    this.candidate = null;
    this.mainLaneAudio = null;
    this.pendingSegments = [];
    if (c.overlapTimer) window.clearTimeout(c.overlapTimer);

    if (c.hardInterrupted) {
      const from = c.cleanFromTs ?? c.duckTs ?? c.onsetTs;
      this.mainLaneAudio = trimAudioFromWallTime(audio, startTs, endTs, from, 450);
      return false;
    }

    const s = this.hooks.settings();
    const segmentMs = endTs - c.onsetTs;
    const seg = { audio, startTs, endTs, trimFrom: c.cleanFromTs ?? c.duckTs ?? c.onsetTs };

    // Echo-only blip with no duck/pause: discard, keep talking.
    if (c.echoSuspect && !c.pauseTs && segmentMs < s.overlapHardMs) {
      this.record(c, '', 'echo', 'ECHO', { decision: endTs, stop: null, stt: null, cls: null });
      this.hooks.clearCapture();
      this.resumeSpeaking(c);
      return true;
    }

    if (!c.confirmed || segmentMs < s.bargeinMinSegmentMs) {
      // Paused by the echo gate: too short to be speech → resume. Paused for sustained speech →
      // stop TTS and hand audio to main-lane STT (no interrupt capture limbo).
      if (c.pauseTs != null && !c.echoPause) {
        return this.stopAndHandOffMain(c, seg, 'duration');
      }
      this.record(c, '', c.echoPause ? 'echo' : 'lexical', c.echoPause ? 'ECHO' : 'NOISE', { decision: endTs, stop: null, stt: null, cls: null });
      this.hooks.clearCapture();
      this.resumeSpeaking(c);
      return true;
    }

    this.deciding = true;
    try {
      return await this.decide(c, seg);
    } finally {
      this.deciding = false;
    }
  }

  private async decide(c: Candidate, seg: Segment): Promise<boolean> {
    const s = this.hooks.settings();
    this.hooks.setState('classifying');
    const currentSentence = this.hooks.player.currentChunkText();
    // Prefer duck/onset cut; fall back to full buffer (Whisper tolerates a little TTS bleed).
    const sttAudio = trimAudioFromWallTime(seg.audio, seg.startTs, seg.endTs, seg.trimFrom, 450);
    let stt: SttResult;
    try {
      stt = await this.hooks.stt(sttAudio);
    } catch (err) {
      this.hooks.log(`barge-in STT failed: ${err instanceof Error ? err.message : err}`);
      if (c.pauseTs != null && !c.echoPause) return this.stopAndHandOffMain(c, seg, 'lexical-interrupt');
      return this.finishResume(c, { tier: 'echo', outcome: 'ECHO', transcript: '', stt: null, cls: null });
    }
    const transcript = stt.text;

    if (!this.hooks.assistantActive()) {
      this.record(c, transcript, 'playback-ended', 'ANSWER', { decision: Date.now(), stop: null, stt, cls: null });
      if (transcript) this.hooks.handoffToMain(transcript, { speechStart: seg.startTs, speechEnd: seg.endTs });
      return true;
    }

    if (!transcript) {
      // Sustained-speech pause with an empty transcript: Chirp often blanks short clips — stop
      // and let main-lane STT retry. Echo pause / echo-suspect duck: it was bleed, resume.
      if (c.pauseTs != null && !c.echoPause) return this.stopAndHandOffMain(c, seg, 'lexical-interrupt', stt);
      if (c.echoSuspect || c.echoPause) {
        return this.finishResume(c, { tier: 'echo', outcome: 'ECHO', transcript: '', stt, cls: null });
      }
    }

    const { outcome, tier, cls } = await this.classify(c, transcript, currentSentence, s);
    if (!this.hooks.assistantActive()) {
      this.record(c, transcript, 'playback-ended', 'ANSWER', { decision: Date.now(), stop: null, stt, cls });
      if (transcript) this.hooks.handoffToMain(transcript, { speechStart: seg.startTs, speechEnd: seg.endTs });
      return true;
    }
    return this.applyOutcome(c, seg, { outcome, tier, transcript, stt, cls, currentSentence });
  }

  private async classify(
    c: Candidate,
    transcript: string,
    currentSentence: string,
    s: Settings,
  ): Promise<{ outcome: BargeinOutcome; tier: BargeinTier; cls: BargeinClassifyResult | null }> {
    const lexical = classifyLexical(transcript, endsWithQuestion(currentSentence));
    let outcome: BargeinOutcome = lexical.outcome;
    let tier: BargeinTier = outcome === 'INTERRUPT' ? 'lexical-interrupt' : 'lexical';
    let cls: BargeinClassifyResult | null = null;
    const words = transcript ? transcript.split(/\s+/).length : 0;

    if (!lexical.confident && s.tierBEnabled && words <= s.tierBMaxWords) {
      try {
        cls = await this.hooks.classify(c.turnId, transcript, currentSentence);
        outcome = cls.outcome;
        tier = 'llm';
      } catch (err) {
        this.hooks.log(`Tier B classify failed: ${err instanceof Error ? err.message : err}`);
        // Vertex Tier B can fail open; if we already paused, treat as interrupt.
        if (c.pauseTs != null) outcome = 'INTERRUPT';
      }
    }
    if (outcome === 'UNCLEAR') {
      this.unclearCount++;
      // After a real pause, first UNCLEAR should not resume TTS (Vertex free-form labels).
      if (c.pauseTs != null || this.unclearCount >= 2) {
        outcome = 'INTERRUPT';
        tier = 'escalation';
      } else {
        outcome = 'BACKCHANNEL';
      }
    }
    return { outcome, tier, cls };
  }

  private applyOutcome(c: Candidate, seg: Segment, d: Decision & { currentSentence: string }): boolean {
    const decision = Date.now();
    const marks = { speechStart: seg.startTs, speechEnd: seg.endTs, cleanFrom: seg.trimFrom };
    const hasText = d.transcript.trim().length > 0;
    switch (d.outcome) {
      case 'BACKCHANNEL':
        return this.finishResume(c, d, () => {
          if (hasText) this.hooks.aside(c.turnId, d.transcript, 'BACKCHANNEL', d.currentSentence);
        });
      case 'NOISE': {
        if (c.pauseTs == null || c.echoPause) return this.finishResume(c, d);
        const tier: BargeinTier = d.tier === 'llm' ? 'llm' : 'lexical-interrupt';
        if (!hasText) return this.stopAndHandOffMain(c, seg, tier, d.stt);
        this.record(c, d.transcript, tier, 'INTERRUPT', { decision, stop: decision, stt: d.stt, cls: d.cls });
        this.hooks.hardInterrupt(c.turnId, tier, d.transcript, marks);
        return true;
      }
      case 'ANSWER':
        this.record(c, d.transcript, d.tier, d.outcome, { decision, stop: decision, stt: d.stt, cls: d.cls });
        if (hasText) {
          this.hooks.softYield(c.turnId, d.tier, d.transcript, marks);
          return true;
        }
        if (c.pauseTs != null) return this.stopAndHandOffMain(c, seg, d.tier, d.stt);
        this.hooks.hardInterrupt(c.turnId, d.tier, null, marks);
        return true;
      case 'INTERRUPT':
      default:
        this.record(c, d.transcript, d.tier, 'INTERRUPT', { decision, stop: decision, stt: d.stt, cls: d.cls });
        if (hasText) {
          this.hooks.hardInterrupt(c.turnId, d.tier, d.transcript, marks);
          return true;
        }
        if (c.pauseTs != null) return this.stopAndHandOffMain(c, seg, d.tier, d.stt);
        this.hooks.hardInterrupt(c.turnId, d.tier, null, marks);
        return true;
    }
  }

  /**
   * Non-interrupt decision (echo / backchannel / noise). If the user kept talking while we were
   * deciding, they want the floor: stop and hand the queued audio to main-lane STT instead of
   * resuming over them.
   */
  private finishResume(c: Candidate, d: Decision, onResume?: () => void): boolean {
    const pending = this.takePendingSegments();
    if (pending) {
      const decision = Date.now();
      this.record(c, d.transcript, 'escalation', 'INTERRUPT', { decision, stop: decision, stt: d.stt, cls: d.cls });
      this.hooks.hardInterrupt(c.turnId, 'escalation', null, { speechStart: pending.startTs, speechEnd: pending.endTs, cleanFrom: pending.startTs });
      this.mainLaneAudio = pending.audio;
      return false;
    }
    this.hooks.clearCapture();
    this.resumeSpeaking(c);
    onResume?.();
    this.record(c, d.transcript, d.tier, d.outcome, { decision: Date.now(), stop: null, stt: d.stt, cls: d.cls });
    return true;
  }

  /**
   * Stop assistant playback and hand the segment to main-lane STT.
   * Used when barge-in STT is empty/failed after a pause — opening interrupt capture with no
   * seed leaves duplex stuck on Listening (ambient energy never endpoints).
   */
  private stopAndHandOffMain(c: Candidate, seg: Segment, tier: BargeinTier, stt: SttResult | null = null): false {
    const decision = Date.now();
    this.record(c, stt?.text ?? '', tier, 'INTERRUPT', { decision, stop: decision, stt, cls: null });
    this.hooks.hardInterrupt(c.turnId, tier, null, { speechStart: seg.startTs, speechEnd: seg.endTs, cleanFrom: seg.trimFrom });
    const pending = this.takePendingSegments();
    const own = trimAudioFromWallTime(seg.audio, seg.startTs, seg.endTs, seg.trimFrom, 450);
    this.mainLaneAudio = pending ? concatFloat32([own, pending.audio]) : own;
    return false;
  }

  /** Not an interruption: unfreeze (if paused), restore volume, and report speaking. */
  private resumeSpeaking(c: Candidate) {
    if (c.pauseTs !== null) this.hooks.player.resumePlayback();
    this.hooks.player.unduck();
    if (this.hooks.assistantActive()) this.hooks.setPlaybackBias(true);
    this.hooks.setState('speaking');
  }

  /**
   * Drop the active barge-in candidate.
   * `resume: false` when the caller is hard-stopping playback — never unfreeze audio first
   * (that caused a blip of the assistant continuing after interrupt).
   * `keepHardInterrupted: true` keeps a duration-gate candidate alive so the upcoming VAD
   * speechEnd still hands its trimmed audio to the main lane (see {@link takeMainLaneAudio}).
   */
  cancel(opts?: { resume?: boolean; keepHardInterrupted?: boolean }) {
    const resume = opts?.resume !== false;
    const c = this.candidate;
    if (c?.overlapTimer) window.clearTimeout(c.overlapTimer);
    if (resume && c?.pauseTs != null) this.hooks.player.resumePlayback();
    if (c?.confirmed || c?.duckTs != null) this.hooks.player.unduck();
    this.deciding = false;
    this.pendingSegments = [];
    if (opts?.keepHardInterrupted && c?.hardInterrupted) {
      c.overlapTimer = null;
      return;
    }
    this.candidate = null;
  }

  /** VAD misfire after onset: drop the candidate and restore playback (no STT). */
  cancelShort() {
    const c = this.candidate;
    if (!c) return;
    if (c.overlapTimer) window.clearTimeout(c.overlapTimer);
    if (c.pauseTs != null) this.hooks.player.resumePlayback();
    if (c.confirmed || c.duckTs != null) this.hooks.player.unduck();
    this.candidate = null;
    this.pendingSegments = [];
    this.hooks.clearCapture();
    if (this.hooks.assistantActive()) {
      this.hooks.setPlaybackBias(true);
      this.hooks.setState('speaking');
    }
  }

  private record(
    c: Candidate,
    transcript: string,
    tier: BargeinTier,
    outcome: BargeinOutcome,
    t: { decision: number | null; stop: number | null; stt: SttResult | null; cls: BargeinClassifyResult | null },
  ) {
    const ev: ArbiterEvent = {
      id: newId('ae'),
      sessionId: '',
      turnId: c.turnId,
      ts: Date.now(),
      transcript,
      currentSentence: this.hooks.player.currentChunkText(),
      tier,
      outcome,
      onsetToDuckMs: c.duckTs ? c.duckTs - c.onsetTs : null,
      onsetToPauseMs: c.pauseTs ? c.pauseTs - c.onsetTs : null,
      onsetToDecisionMs: t.decision ? t.decision - c.onsetTs : null,
      onsetToStopMs: t.stop ? t.stop - c.onsetTs : null,
      sttMs: t.stt?.sttMs ?? null,
      sttQueueWaitMs: t.stt?.queueWaitMs ?? null,
      classifyMs: t.cls?.classifyMs ?? null,
      classifyQueueWaitMs: t.cls?.queueWaitMs ?? null,
      feedback: null,
    };
    this.hooks.recordEvent(ev);
  }
}

/**
 * Count syllable-like energy peaks: local maxima above `threshold` that are separated by a trough
 * dipping below 60% of the lower neighbouring peak, at least two frames (~64 ms) apart.
 */
export function countSyllablePeaks(energy: number[], threshold: number): number {
  if (energy.length < 3) return 0;
  const sm: number[] = [];
  for (let i = 0; i < energy.length; i++) {
    const a = energy[i - 1] ?? energy[i];
    const b = energy[i + 1] ?? energy[i];
    sm.push((a + energy[i] + b) / 3);
  }
  let peaks = 0;
  let lastPeakIdx = -10;
  let lastPeakVal = 0;
  let troughSinceLast = Infinity;
  for (let i = 1; i < sm.length - 1; i++) {
    const v = sm[i];
    troughSinceLast = Math.min(troughSinceLast, v);
    const isMax = v >= sm[i - 1] && v > sm[i + 1] && v > threshold;
    if (!isMax) continue;
    if (i - lastPeakIdx < 2) continue;
    if (peaks > 0 && troughSinceLast > 0.6 * Math.min(v, lastPeakVal)) continue;
    peaks++;
    lastPeakIdx = i;
    lastPeakVal = v;
    troughSinceLast = Infinity;
  }
  return peaks;
}
